import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useFetch } from '../hooks/useFetchAuth';
import SortableTable from '../components/SortableTable';
import MappingConfigView from '../components/MappingConfigView';
import IngestionSummary from '../components/IngestionSummary';
import LoadActions from '../components/LoadActions';
import { Download, Maximize2, Rows3, WrapText as WrapTextIcon, X } from 'lucide-react';

const HistoryDetail = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedPathIndex, setExpandedPathIndex] = useState(null);

  const {
    data: mappingDetail,
    isLoading: mappingLoading,
    error: mappingError,
  } = useFetch(["history-mapping", uuid], `/api/history/${uuid}/mapping`);

  const {
    data: snowflakeDetail,
    isLoading: dataLoading,
    error: dataError,
  } = useFetch(["history-data", uuid], `/api/history/${uuid}/data`);

  const {
    data: filesInfoRaw,
    isLoading: filesLoading,
    error: filesFetchError,
  } = useFetch(["history-files", uuid], `/api/history/${uuid}/files`, {
    retry: false,
  });

  const loading = mappingLoading || dataLoading;
  // The routes answer 404 to an unknown or deleted load, and to another user's load
  const error = [mappingError, dataError].some((e) => e?.status === 404)
    ? "Chargement introuvable : il n'existe pas, a été supprimé, ou vous n'y avez pas accès."
    : mappingError?.message || dataError?.message;

  const filesError =
    filesFetchError?.status === 404 ? null : filesFetchError?.message;
  const filesInfo = filesFetchError?.status === 404 ? null : filesInfoRaw;
  const timeZoneLabel = useMemo(() => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
      const parts = formatter.formatToParts(new Date());
      const tz = parts.find((part) => part.type === 'timeZoneName');
      return tz ? tz.value : formatter.format(new Date()).split(' ').pop();
    } catch (e) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  }, []);

  const historyItem = location.state?.historyItem;

  const formatDateTime = useCallback((value) => {
    if (!value) return 'N/A';
    try {
      const date = new Date(value);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return value;
    }
  }, []);





  const handleExpandPath = (index) => {
    setExpandedPathIndex(index);
  };

  const handleCloseExpanded = () => {
    setExpandedPathIndex(null);
  };

  // Field descriptions and groups (requis, importants, optionnels), as on the mapping screen
  const mappingShopSign = mappingDetail?.metadata?.shop_sign;
  const mappingClient = mappingDetail?.metadata?.client;
  const { data: choicesData } = useFetch(
    ['mappingChoices', mappingShopSign, mappingClient],
    `/api/mapping-choices/${mappingShopSign}/${mappingClient}`,
    { enabled: Boolean(mappingShopSign && mappingClient) },
  );

  const originalFilename = useMemo(() => historyItem?.filename || null, [historyItem?.filename]);

  const processedFilename = useMemo(() => {
    if (!originalFilename) return null;
    const dotIndex = originalFilename.lastIndexOf('.');
    if (dotIndex > 0) {
      const base = originalFilename.slice(0, dotIndex);
      const extension = originalFilename.slice(dotIndex);
      return `${base}_processed${extension}`;
    }
    return `${originalFilename}_processed`;
  }, [originalFilename]);

  const handleDownload = useCallback((url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const renderPrimaryFile = (url, label, fileNameHint) => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{label}</p>
        {fileNameHint && (
          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{fileNameHint}</p>
        )}
        {!url && (
          <p className="text-xs text-red-500 dark:text-red-400 mt-1">File unavailable</p>
        )}
      </div>
      {url && (
        <button
          onClick={() => handleDownload(url)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
          type="button"
        >
          <Download className="w-4 h-4" />
          Download
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/processor/history')}
            className="inline-flex items-center px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            ← Retour à l'historique
          </button>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              ID de l'historique: <span className="font-mono">{uuid}</span>
            </div>
            <LoadActions uuid={uuid} />
          </div>
        </div>

        {/* Read from the server, so it shows on a direct link too (it used to need the History page's row) */}
        <IngestionSummary uuid={uuid} />

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="ml-3 text-gray-600 dark:text-gray-300">Chargement des détails…</p>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Fichiers</h2>
                {filesInfo?.expires_at && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Liens expirés à {formatDateTime(filesInfo.expires_at)}
                  </span>
                )}
              </div>

              {filesLoading ? (
                <div className="flex items-center gap-3 py-4">
                  <div className="w-5 h-5 border-2 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Chargement des fichiers…</span>
                </div>
              ) : filesError ? (
                <div className="text-sm text-red-500 dark:text-red-400">{filesError}</div>
              ) : filesInfo ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {renderPrimaryFile(filesInfo.input, 'Input Excel', originalFilename ? `${originalFilename} (input copy)` : null)}
                  {renderPrimaryFile(filesInfo.output, 'Output Excel', processedFilename)}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Aucun fichier téléchargeable disponible.</p>
              )}
            </section>

            <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-semibold">Mapping</h2>
                {mappingDetail?.mapping_id && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {mappingDetail.mapping_name} · créé le {formatDateTime(mappingDetail.metadata?.created_at)} · n° {mappingDetail.mapping_id}
                  </p>
                )}
              </div>

              <div>
                {mappingDetail?.config ? (
                  <MappingConfigView
                    config={mappingDetail.config}
                    currentSheet={mappingDetail.sheet_name}
                    choices={choicesData?.choices}
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune configuration de mapping disponible.</p>
                )}
              </div>
            </section>

            <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Données Snowflake</h2>
                {snowflakeDetail?.limit && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Affichage jusqu'à {snowflakeDetail.limit} lignes
                  </p>
                )}
              </div>

              {snowflakeDetail?.paths?.length ? (
                <div className="space-y-6">
                  {snowflakeDetail.paths.map((path, index) => {
                    const columns = (path.columns || []).map((col) => ({ key: col, label: col }));
                    const hasData = path.rows && path.rows.length > 0;

                    return (
                      <div key={path.blob_path} className="space-y-3">
                        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                          <p><span className="font-semibold">Nom de la table:</span> {path.table_name}</p>
                          <p><span className="font-semibold">Chemin du blob:</span> {path.blob_path}</p>
                          <p><span className="font-semibold">Somme de la table:</span> {path.sum_quantity ?? 'N/A'}</p>
                        </div>

                        {hasData && columns.length ? (
                          <>
                            <div className="border border-gray-200 dark:border-gray-700 rounded">
                              <SortableTable
                                data={path.rows || []}
                                columns={columns}
                                emptyMessage="Aucune ligne trouvée pour ce chemin de blob."
                                disableFilters
                                initialSortBy={{ key: columns[0].key, direction: 'ascending' }}
                                density="compact"
                                maxHeight="30em"
                              />
                            </div>
                            <div className="flex justify-end">
                              <button
                                onClick={() => handleExpandPath(index)}
                                className="inline-flex items-center px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 gap-2"
                                type="button"
                              >
                                <Maximize2 className="w-4 h-4" />
                                Agrandir la table
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Aucune ligne trouvée pour ce chemin de blob.
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {snowflakeDetail.errors && snowflakeDetail.errors.length > 0 && (
                    <div className="text-xs text-yellow-700 dark:text-yellow-400 border-t border-yellow-500/30 pt-3 space-y-1">
                      <p className="font-semibold">Avertissements</p>
                      {snowflakeDetail.errors.map((warn, index) => (
                        <p key={`${warn.blob_path || 'error'}-${index}`}>
                          {warn.blob_path ? `${warn.blob_path}: ` : ''}{warn.message}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aucune donnée Snowflake associée à cette entrée d'historique.
                </p>
              )}
            </section>
          </>
        )}
      </div>
      </div>
      {expandedPathIndex !== null && snowflakeDetail?.paths?.[expandedPathIndex] && (
        <ExpandedTableOverlay
          path={snowflakeDetail.paths[expandedPathIndex]}
          onClose={handleCloseExpanded}
          timeZoneLabel={timeZoneLabel}
        />
      )}
    </>
  );
};

const ExpandedTableOverlay = ({ path, onClose, timeZoneLabel }) => {
  const columns = useMemo(() => (path.columns || []).map((col) => ({ key: col, label: col })), [path.columns]);
  const hasData = path.rows && path.rows.length > 0 && columns.length;
  const [isCompact, setIsCompact] = useState(true);
  const [wrapContent, setWrapContent] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex flex-col">
      <div className="bg-white dark:bg-gray-900 flex-1 m-4 rounded-lg shadow-2xl overflow-hidden flex flex-col max-w-[95vw] self-center">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 gap-4 flex-wrap">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 break-all">{path.blob_path}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Fuseau horaire: {timeZoneLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {hasData && (
              <>
                <button
                  onClick={() => setIsCompact((prev) => !prev)}
                  className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 inline-flex items-center gap-2"
                  type="button"
                >
                  <Rows3 className="w-4 h-4" />
                  {isCompact ? 'Lignes confortables' : 'Lignes compactes'}
                </button>
                <button
                  onClick={() => setWrapContent((prev) => !prev)}
                  className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 inline-flex items-center gap-2"
                  type="button"
                >
                  <WrapTextIcon className="w-4 h-4" />
                  {wrapContent ? 'Texte collé' : 'Texte enveloppé'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 inline-flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Fermer
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden p-4">
          {hasData ? (
            <div className="h-full border border-gray-200 dark:border-gray-700 rounded resize-y overflow-hidden min-h-[300px] flex flex-col">
              <SortableTable
                data={path.rows || []}
                columns={columns}
                emptyMessage="Aucune donnée disponible pour ce chemin de blob."
                exportable
                exportFilename={`history_${path.table_name || 'table'}.csv`}
                density={isCompact ? 'compact' : 'comfortable'}
                wrap={wrapContent}
                maxHeight={null}
              />
            </div>
          ) : (
            <div className="h-full border border-dashed border-gray-300 dark:border-gray-700 rounded flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              Aucune donnée disponible pour ce chemin de blob.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryDetail;
